import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import authService from '../services/auth.service';
import oauthService from '../services/oauth.service';
import OTPVerificationModal from '../components/auth/OTPVerificationModal';
import CompanyBanner from '../components/company/CompanyBanner';
import axiosInstance from '../config/axios.config';
import Swal from 'sweetalert2';

// Constants
const TOAST_DURATIONS = {
    SUCCESS: 3000,
    INFO: 3000,
    WARNING: 6000,
    ERROR: 5000
};

const REDIRECT_DELAY = 1500;

// Error handling constants
const ERROR_TYPES = {
    PASSWORD: 'password',
    EMAIL: 'email',
    VALIDATION: 'validation'
};

const Register = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const [searchParams] = useSearchParams();
    const companyToken = searchParams.get('company');
    const advisorToken = searchParams.get('advisor'); // MLM Phase 2: Referral link token

    // Helper: Build URL with preserved tokens
    const buildAuthUrl = (path) => {
        const params = [];
        if (companyToken) params.push(`company=${companyToken}`);
        if (advisorToken) params.push(`advisor=${advisorToken}`);
        return params.length > 0 ? `${path}?${params.join('&')}` : path;
    };

    // Form state
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        mobile_number: '',
        acceptTerms: false
    });

    // UI state
    const [loading, setLoading] = useState(false);
    const [oauthLoading, setOauthLoading] = useState(false);

    // Company state
    const [companyInfo, setCompanyInfo] = useState(null);
    const [loadingCompany, setLoadingCompany] = useState(false);
    const [companyError, setCompanyError] = useState(null);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [googleButtonHover, setGoogleButtonHover] = useState(false);

    // MLM Phase 2: Referrer state
    const [referrerInfo, setReferrerInfo] = useState(null);
    const [loadingReferrer, setLoadingReferrer] = useState(false);
    const [referrerError, setReferrerError] = useState(null);

    // OTP state
    const [emailVerified, setEmailVerified] = useState(false);
    const [verifiedOtp, setVerifiedOtp] = useState('');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [passwordFocused, setPasswordFocused] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);

    // Clear errors when component mounts
    useEffect(() => {
        setErrors({});
        setSubmitted(false);
    }, []);

    // Fetch company info if token is present
    useEffect(() => {
        if (companyToken) {
            fetchCompanyInfo(companyToken);
        }
    }, [companyToken]);

    // MLM Phase 2: Fetch referrer info if advisor token is present
    useEffect(() => {
        if (advisorToken) {
            fetchReferrerInfo(advisorToken);
        }
    }, [advisorToken]);

    const fetchCompanyInfo = async (token) => {
        setLoadingCompany(true);
        setCompanyError(null);
        try {
            const response = await axiosInstance.get(`/register/company-info?token=${token}`);
                debugger;
            if (response.data.success && response.data.valid) {
                setCompanyInfo({
                    id: response.data.company_id,
                    name: response.data.company_name,
                    email: response.data.company_email
                });
            } else {
                setCompanyError('Invalid or expired registration link');
            }
        } catch (error) {
            setCompanyError(error.response?.data?.message || 'Failed to verify company link');
        } finally {
            setLoadingCompany(false);
        }
    };

    // MLM Phase 2: Fetch referrer info from advisor token
    const fetchReferrerInfo = async (token) => {
        setLoadingReferrer(true);
        setReferrerError(null);
        try {
            const response = await axiosInstance.get(`/auth/validate-referral-link/${token}`);

            if (response.data.success && response.data.valid) {
                setReferrerInfo({
                    name: response.data.referrer_name,
                    companyName: response.data.company_name || null
                });
            } else {
                setReferrerError(response.data.message || 'Invalid referral link');
            }
        } catch (error) {
            setReferrerError(error.response?.data?.message || 'Failed to verify referral link');
        } finally {
            setLoadingReferrer(false);
        }
    };

    // Handle input change
    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'acceptTerms' ? checked : value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    // Optimized email change handler
    const handleEmailChange = useCallback((e) => {
        handleChange(e);
        if (emailVerified) {
            setEmailVerified(false);
            setVerifiedOtp('');
            // Clear email-related errors
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.email;
                delete newErrors.general;
                return newErrors;
            });
        }
    }, [emailVerified]);

    // Password validation helper
    const getPasswordValidation = (password) => {
        return {
            minLength: password.length >= 8,
            hasUpperCase: /[A-Z]/.test(password),
            hasLowerCase: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        }

        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
        }

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else {
            if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters';
            }
        }

        if (!formData.password_confirmation) {
            newErrors.password_confirmation = 'Please confirm your password';
        } else if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'Passwords do not match';
        }

        if (!formData.acceptTerms) {
            newErrors.acceptTerms = 'You must accept the terms and conditions';
        }

        // Mobile number validation (optional but if provided, must be valid)
        if (formData.mobile_number) {
            const cleanedNumber = formData.mobile_number.replace(/[^0-9]/g, '');
            if (cleanedNumber.length !== 10) {
                newErrors.mobile_number = 'Please enter a valid 10-digit mobile number';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle sending OTP
    const handleSendOTP = async () => {
        // Basic email validation
        if (!formData.email) {
            setErrors({ ...errors, email: 'Please enter your email first' });
            return;
        }

        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setErrors({ ...errors, email: 'Please enter a valid email' });
            return;
        }

        setSendingOtp(true);
        setErrors(prev => ({...prev, email: null}));

        try {
            const response = await axiosInstance.post('/auth/send-otp', {
                email: formData.email
            });

            if (response.data.success) {
                // Removed toast - just open OTP modal directly
                setShowOtpModal(true);
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to send OTP';

            // If email already registered, show only field error (no toast)
            if (message.includes('already registered')) {
                setErrors(prev => ({...prev, email: message}));
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: message,
                    life: TOAST_DURATIONS.ERROR
                });
            }
        } finally {
            setSendingOtp(false);
        }
    };

    // Handle OTP verification success
    const handleOtpVerified = (otp) => {
        setVerifiedOtp(otp);
        setEmailVerified(true);
        // Removed toast - success message shows below email field only
    };

    // Handle OTP resend
    const handleOtpResend = async () => {
        // Clear previous email errors
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.email;
            return newErrors;
        });
        
        try {
            const response = await axiosInstance.post('/auth/send-otp', {
                email: formData.email
            });

            if (response.data.success) {
                toast.current.show({
                    severity: 'info',
                    summary: 'OTP Resent',
                    detail: 'A new verification code has been sent to your email',
                    life: TOAST_DURATIONS.INFO
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to resend OTP. Please try again.',
                life: TOAST_DURATIONS.ERROR
            });
        }
    };

    // Handle traditional registration
    const handleRegister = async (e) => {
        e.preventDefault();
        setSubmitted(true);

        if (!validateForm()) {
            return;
        }

        if (!emailVerified) {
            toast.current.show({
                severity: 'error',
                summary: 'Email Not Verified',
                detail: 'Please verify your email before registering',
                life: TOAST_DURATIONS.ERROR
            });
            return;
        }

        setLoading(true);

        try {
            // Include OTP in registration data
            const registrationData = {
                ...formData,
                otp: verifiedOtp
            };

            // Add company_token if present
            if (companyToken && companyInfo) {
                registrationData.company_token = companyToken;
            }

            // MLM Phase 2: Add advisor_token if present
            if (advisorToken && referrerInfo) {
                registrationData.advisor_token = advisorToken;
            }

            const result = await authService.registerV2(registrationData);

            if (result.success) {
                // Show simple success message
                Swal.fire({
                    title: 'Registration Successful!',
                    text: 'Welcome to INTERESTNG',
                    icon: 'success',
                    timer: 3000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    allowEscapeKey: false
                }).then((result) => {
                    // Close SweetAlert immediately before navigation
                    Swal.close();
                    navigate('/app/dashboard');
                });

                // Fallback redirect with SweetAlert close
                setTimeout(() => {
                    Swal.close();
                    navigate('/app/dashboard');
                }, 2800); // Close slightly before timer ends
            } else {
                // Handle validation errors from server
                if (result.errors) {
                    // Laravel returns errors as arrays, extract first error for each field
                    const formattedErrors = {};
                    Object.keys(result.errors).forEach(key => {
                        formattedErrors[key] = Array.isArray(result.errors[key])
                            ? result.errors[key][0]
                            : result.errors[key];
                    });
                    setErrors(prev => ({...prev, ...formattedErrors}));
                }

                // Check if it's an OTP-related error and reset email verification
                const msg = result.message || '';
                if (msg.toLowerCase().includes('otp') ||
                    msg.toLowerCase().includes('expired') ||
                    msg.toLowerCase().includes('verify your email')) {
                    setEmailVerified(false);
                    setVerifiedOtp('');
                }

                toast.current.show({
                    severity: 'error',
                    summary: 'Registration Failed',
                    detail: result.message || 'Failed to create account',
                    life: TOAST_DURATIONS.ERROR
                });
            }
        } catch (error) {
            // Log error for debugging (in development only)
            if (process.env.NODE_ENV === 'development') {
                console.error('Registration error:', error);
            }

            const errorMessage = error.response?.data?.message || error.message || 'Registration failed. Please try again.';

            // Check if it's an OTP-related error and reset email verification
            if (errorMessage.toLowerCase().includes('otp') ||
                errorMessage.toLowerCase().includes('expired') ||
                errorMessage.toLowerCase().includes('verify your email')) {
                setEmailVerified(false);
                setVerifiedOtp('');
                setErrors(prev => ({
                    ...prev,
                    email: 'Your verification has expired. Please verify your email again.'
                }));
            }

            // Handle backend validation errors
            if (error.response?.data?.errors) {
                // Laravel returns errors as arrays, extract first error for each field
                const validationErrors = error.response.data.errors;
                const formattedErrors = {};
                Object.keys(validationErrors).forEach(key => {
                    formattedErrors[key] = Array.isArray(validationErrors[key])
                        ? validationErrors[key][0]
                        : validationErrors[key];
                });
                setErrors(prev => ({...prev, ...formattedErrors}));
            } else {
                const errorType = error.response?.data?.type;

                // Use error type from server or fallback to message analysis
                if (errorType === ERROR_TYPES.PASSWORD || errorMessage.toLowerCase().includes(ERROR_TYPES.PASSWORD)) {
                    setErrors(prev => ({
                        ...prev,
                        password: errorMessage
                    }));
                } else if (errorType === ERROR_TYPES.EMAIL || errorMessage.toLowerCase().includes(ERROR_TYPES.EMAIL)) {
                    setErrors(prev => ({
                        ...prev,
                        email: errorMessage
                    }));
                } else {
                    setErrors(prev => ({
                        ...prev,
                        general: errorMessage
                    }));
                }
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle Google OAuth registration
    const handleGoogleRegister = async () => {
        setOauthLoading(true);

        try {
            // Pass both company_token and advisor_token (if present)
            const result = await oauthService.initiateGoogleOAuth(companyToken, advisorToken);

            if (result.success) {
                // Redirect to Google OAuth
                window.location.href = result.oauth_url;
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'OAuth Failed',
                    detail: result.message || 'Failed to initiate Google authentication',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('OAuth error:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to connect with Google. Please try again.',
                life: 5000
            });
        } finally {
            setOauthLoading(false);
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <div className="flex min-h-screen">
                {/* Left Panel - Form Section */}
                <div className="w-full lg:w-4 min-h-screen lg:h-screen text-center px-4 py-4 lg:px-6 lg:py-4 flex flex-column justify-content-center lg:justify-content-between surface-ground overflow-y-auto">
                    {/* Logo - Hidden on mobile, shown on desktop */}
                    <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="mx-auto hidden lg:block" style={{ height: '3rem', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />

                    {/* Form Section */}
                    <div className="flex flex-column align-items-center gap-2">
                        {/* Logo - Shown on mobile only */}
                        <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="block lg:hidden mb-2" style={{ height: '3rem', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />

                        {/* Company Banner - Show if company info is valid */}
                        {companyInfo && !loadingCompany && (
                            <div className="w-full max-w-25rem mb-2">
                                <CompanyBanner companyName={companyInfo.name} />
                            </div>
                        )}

                        {/* Loading Company Info */}
                        {loadingCompany && (
                            <div className="w-full max-w-25rem mb-2">
                                <Message severity="info" text="Verifying company registration link..." />
                            </div>
                        )}

                        {/* Company Error */}
                        {companyError && (
                            <div className="w-full max-w-25rem mb-2">
                                <Message severity="warn" text={companyError} />
                            </div>
                        )}

                        {/* MLM Phase 2: Referrer Banner - Show if referrer info is valid */}
                        {referrerInfo && !loadingReferrer && (
                            <div className="w-full max-w-25rem mb-2">
                                <Message
                                    severity="success"
                                    content={
                                        <div className="flex align-items-center">
                                            <i className="pi pi-users mr-2"></i>
                                            <div>
                                                <span className="font-semibold">Joining {referrerInfo.name}'s Network</span>
                                                {referrerInfo.companyName && (
                                                    <span className="block text-sm mt-1">{referrerInfo.companyName}</span>
                                                )}
                                            </div>
                                        </div>
                                    }
                                />
                            </div>
                        )}

                        {/* Loading Referrer Info */}
                        {loadingReferrer && (
                            <div className="w-full max-w-25rem mb-2">
                                <Message severity="info" text="Verifying referral link..." />
                            </div>
                        )}

                        {/* Referrer Error */}
                        {referrerError && (
                            <div className="w-full max-w-25rem mb-2">
                                <Message severity="warn" text={referrerError} />
                            </div>
                        )}

                        <div className="mb-1 text-center">
                            <h2 className="m-0 text-xl">Register</h2>
                        </div>

                        <div className="flex flex-column gap-1 w-full max-w-25rem">
                            
                            <Button
                                className="w-full mb-1 p-button-outlined p-button-sm"
                                type="button"
                                onClick={handleGoogleRegister}
                                loading={oauthLoading}
                                disabled={loading}
                                style={{
                                    backgroundColor: '#ffffff',
                                    color: '#3c4043',
                                    borderColor: googleButtonHover ? '#d2e3fc' : '#dadce0',
                                    borderRadius: '4px',
                                    padding: '12px 16px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    fontFamily: 'Roboto, sans-serif',
                                    boxShadow: googleButtonHover 
                                        ? '0 1px 3px rgba(60,64,67,0.3), 0 4px 8px rgba(60,64,67,0.15)'
                                        : '0 1px 2px rgba(60,64,67,0.3), 0 1px 3px rgba(60,64,67,0.15)',
                                    transition: 'all 0.2s ease-in-out'
                                }}
                                onMouseEnter={() => setGoogleButtonHover(true)}
                                onMouseLeave={() => setGoogleButtonHover(false)}
                            >
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    gap: '12px',
                                    width: '100%',
                                    textAlign: 'center'
                                }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24">
                                        <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                        <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                        <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                        <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                    </svg>
                                    <span style={{ 
                                        color: '#3c4043', 
                                        fontSize: '14px', 
                                        fontWeight: '500',
                                        textAlign: 'center',
                                        display: 'block'
                                    }}>Continue with Google</span>
                                </div>
                            </Button>

                            <div className="flex align-items-center w-full">
                                <div className="border-top-1 surface-border flex-auto"></div>
                                <span className="px-3 text-color-secondary">or</span>
                                <div className="border-top-1 surface-border flex-auto"></div>
                            </div>

                            <div className="field">
                                <IconField iconPosition="left">
                                    <InputIcon className="pi pi-user" />
                                    <InputText
                                        id="first_name"
                                        name="first_name"
                                        type="text"
                                        className={`w-full ${errors.first_name ? 'p-invalid' : ''}`}
                                        placeholder="First Name"
                                        value={formData.first_name}
                                        onChange={handleChange}             
                                        disabled={loading || oauthLoading}
                                        aria-invalid={errors.first_name ? 'true' : 'false'}
                                        aria-describedby={errors.first_name ? 'first_name-error' : null}
                                    />
                                </IconField>
                                {errors.first_name && <small className="p-error" id="first_name-error" role="alert">{errors.first_name}</small>}
                            </div>

                            <div className="field">
                                <IconField iconPosition="left">
                                    <InputIcon className="pi pi-user" />
                                    <InputText
                                        id="last_name"
                                        name="last_name"
                                        type="text"
                                        className={`w-full ${errors.last_name ? 'p-invalid' : ''}`}
                                        placeholder="Last Name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        disabled={loading || oauthLoading}
                                        aria-invalid={errors.last_name ? 'true' : 'false'}
                                        aria-describedby={errors.last_name ? 'last_name-error' : null}
                                    />
                                </IconField>
                                {errors.last_name && <small className="p-error" id="last_name-error" role="alert">{errors.last_name}</small>}
                            </div>

                            <div className="field">
                                <IconField iconPosition="left">
                                    <InputIcon className="pi pi-phone" />
                                    <InputText
                                        id="mobile_number"
                                        name="mobile_number"
                                        type="tel"
                                        className={`w-full ${errors.mobile_number ? 'p-invalid' : ''}`}
                                        placeholder="Mobile Number"
                                        value={formData.mobile_number}
                                        onChange={handleChange}
                                        onInput={(e) => {
                                            // Limit to 10 digits only
                                            e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                                        }}
                                        maxLength={10}
                                        disabled={loading || oauthLoading}
                                        aria-invalid={errors.mobile_number ? 'true' : 'false'}
                                        aria-describedby={errors.mobile_number ? 'mobile_number-error' : null}
                                    />
                                </IconField>
                                {errors.mobile_number && <small className="p-error" id="mobile_number-error" role="alert">{errors.mobile_number}</small>}
                            </div>

                            <div className="field">
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <IconField iconPosition="left">
                                            <InputIcon className="pi pi-envelope" />
                                            <InputText
                                                id="email"
                                                name="email"
                                                type="email"
                                                className="w-full"
                                                placeholder="Email"
                                                value={formData.email}
                                                onChange={handleEmailChange}
                                                disabled={loading || oauthLoading || emailVerified}
                                                aria-invalid={errors.email ? 'true' : 'false'}
                                                aria-describedby={errors.email ? 'email-error' : null}
                                            />
                                        </IconField>
                                    </div>
                                    <Button
                                        type="button"
                                        label="Verify"
                                        icon={emailVerified ? "pi pi-check" : "pi pi-envelope"}
                                        onClick={handleSendOTP}
                                        disabled={emailVerified || !formData.email || sendingOtp || loading || oauthLoading}
                                        loading={sendingOtp}
                                        className="p-button-outlined p-button-sm min-w-min"
                                    />
                                </div>
                                {errors.email && <small className="p-error" id="email-error" role="alert">{errors.email}</small>}
                                {emailVerified && <small className="text-green-600">✓ Email verified successfully!</small>}
                            </div>

                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-lock z-2"></i>
                                <Password
                                    id="password" 
                                    name="password"
                                    className={`w-full ${errors.password ? 'p-invalid' : ''}`}
                                    inputClassName="w-full"
                                    inputStyle={{paddingLeft: '2.5rem'}}
                                    placeholder="Password"
                                    toggleMask
                                    value={formData.password}
                                    onChange={handleChange}
                                    onFocus={() => setPasswordFocused(true)}
                                    onBlur={() => setPasswordFocused(false)}
                                    disabled={loading || oauthLoading}
                                    feedback={false}
                                    aria-invalid={errors.password ? 'true' : 'false'}
                                    aria-describedby={errors.password ? 'password-error' : null}
                                />
                            </span>
                            {errors.password && <small className="p-error" id="password-error" role="alert">{errors.password}</small>}
                            
                            {formData.password && (
                                <small className="text-xs text-color-secondary mt-1 block">
                                    Password should be 8 characters: (A-Z, a-z, numbers, #$%@&)
                                </small>
                            )}

                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-lock z-2"></i>
                                <Password
                                    id="password_confirmation" 
                                    name="password_confirmation"
                                    className={`w-full ${errors.password_confirmation ? 'p-invalid' : ''}`}
                                    inputClassName="w-full"
                                    inputStyle={{paddingLeft: '2.5rem'}}
                                    placeholder="Confirm Password"
                                    toggleMask
                                    value={formData.password_confirmation}
                                    onChange={handleChange}
                                    disabled={loading || oauthLoading}
                                    feedback={false}
                                    aria-invalid={errors.password_confirmation ? 'true' : 'false'}
                                    aria-describedby={errors.password_confirmation ? 'password_confirmation-error' : null}
                                />
                            </span>
                            {errors.password_confirmation && <small className="p-error" id="password_confirmation-error" role="alert">{errors.password_confirmation}</small>}
                            
                            {formData.password && (
                                <small className="text-xs text-color-secondary mt-1 block">
                                    Password should be 8 characters: (A-Z, a-z, numbers, #$%@&)
                                </small>
                            )}
                            
                            {formData.password && formData.password_confirmation && (
                                <div className="mt-1">
                                    {formData.password === formData.password_confirmation ? (
                                        <small className="flex align-items-center gap-2 text-green-600">
                                            <i className="pi pi-check-circle text-sm"></i>
                                            <span className="text-sm">Passwords match</span>
                                        </small>
                                    ) : (
                                        <small className="flex align-items-center gap-2 text-red-500">
                                            <i className="pi pi-times-circle text-sm"></i>
                                            <span className="text-sm">Passwords do not match</span>
                                        </small>
                                    )}
                                </div>
                            )}

                            <div className="flex flex-wrap">
                                <Checkbox
                                    id="acceptTerms"
                                    name="acceptTerms"
                                    checked={formData.acceptTerms}
                                    onChange={handleChange}
                                    disabled={loading || oauthLoading}
                                    className="mr-2"
                                />
                                <label htmlFor="acceptTerms" className="text-900 font-medium mr-2">
                                    I have read the
                                </label>
                                <Link 
                                    to="/app/terms-and-conditions" 
                                    target="_blank" 
                                    className="text-color-secondary font-semibold cursor-pointer hover:text-primary"
                                    aria-label="Terms and Conditions (opens in new tab)"
                                >
                                    Terms and Conditions
                                </Link>
                            </div>
                            {errors.acceptTerms && <small className="p-error" id="acceptTerms-error" role="alert">{errors.acceptTerms}</small>}

                            <Button
                                label="SIGN UP"
                                className="w-full mb-1"
                                onClick={handleRegister}
                                loading={loading}
                                disabled={!emailVerified || loading || oauthLoading || !formData.acceptTerms}
                                aria-describedby="signup-button-help"
                            />

                            <div className="text-center">
                                <span className="font-semibold text-color-secondary">
                                    Already have an account? <Link to={buildAuthUrl('/app/login')} className="font-semibold cursor-pointer primary-color">Login</Link>
                                </span>
                                {!formData.acceptTerms && emailVerified && (
                                    <small id="signup-button-help" className="text-500 block mt-1">
                                        Please accept terms and conditions
                                    </small>
                                )}
                                {errors.general && <small className="p-error block mt-1" role="alert">{errors.general}</small>}

                                {/* Multi-user Registration Link */}
                                <div className="mt-3 pt-3 border-top-1 surface-border">
                                    <span className="font-semibold text-color-secondary">
                                        <i className="pi pi-building mr-2"></i>
                                        Registering as company? <Link to="/app/register/company" className="font-semibold cursor-pointer primary-color">Multi-user Registration</Link>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Right Panel - Hero Section */}
                <div className="w-8 hidden lg:flex flex-column justify-content-center align-items-center px-6 py-6 auth-hero-section">
                    <div className="text-center">
                        <span className="block text-white text-7xl font-bold mb-4">
                            INTERESTNG
                        </span>
                        <span className="block text-white text-5xl font-semibold mb-4">
                            Account
                        </span>
                        <span className="block text-white text-xl line-height-3">
                            Comprehensive credit reports and analysis
                            <br />at your fingertips. Secure, fast, and reliable
                            <br />financial data management.
                        </span>
                    </div>
                </div>
            </div>

            {/* OTP Verification Modal */}
            <OTPVerificationModal
                visible={showOtpModal}
                onHide={() => setShowOtpModal(false)}
                email={formData.email}
                onVerified={handleOtpVerified}
                onResend={handleOtpResend}
            />
        </>
    );
};

export default Register;